// src/Market.js
import React, { useState, useMemo } from "react";
import TeamRow from "./TeamRow";
import TeamDetail from "./TeamDetail";
import ConferenceFilter from "./ConferenceFilter";

const tableStyle = {
  borderCollapse: "collapse",
  width: "100%",
  fontFamily: "system-ui, -apple-system, sans-serif",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  borderRadius: "8px",
  overflow: "hidden",
};

const thTdStyle = {
  border: "1px solid #e5e7eb",
  padding: "10px 14px",
  textAlign: "left",
};

const sortableHeaderStyle = {
  ...thTdStyle,
  backgroundColor: "#f9fafb",
  fontWeight: "600",
  color: "#374151",
  cursor: "pointer",
  userSelect: "none",
};

const recapBoxStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "1rem",
  marginBottom: "1.5rem",
};

const recapCardStyle = {
  background: "#ffffff",
  padding: "0.85rem 1.1rem",
  borderRadius: "8px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  border: "1px solid #e5e7eb",
};

const Market = ({
  teams = [],
  cash = 0,
  buyShares,
  sellShares,
  addCash,
  netDemand = {},
  marketOpen = true,
  mostBought: propMostBought = [],
  mostSold: propMostSold = [],
}) => {
  const [quantities, setQuantities] = useState({});
  const [selectedConference, setSelectedConference] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState("price");
  const [sortDirection, setSortDirection] = useState("desc");

  // Safe helper getters for property compatibility (supports camelCase & snake_case)
  const getTeamKey = (t) => t.id || t.name;
  const getAvailableShares = (t) => t.availableShares ?? t.available_shares ?? 1000;
  
  const getTeamLogo = (t) => {
    if (!t) return "";
    let logo = t.logo_url || t.logoUrl || t.logo || "";
    if (logo.startsWith("http://")) {
      logo = logo.replace("http://", "https://");
    }
    return logo;
  };

  // Handle column header clicks for sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(field === "name" ? "asc" : "desc");
    }
  };

  // Render sorting arrows next to table headers
  const renderSortIndicator = (field) => {
    if (sortField !== field) {
      return <span style={{ color: "#9ca3af", marginLeft: "6px", fontSize: "0.8rem" }}>↕</span>;
    }
    return (
      <span style={{ color: "#2563eb", marginLeft: "6px", fontSize: "0.85rem", fontWeight: "bold" }}>
        {sortDirection === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  // Derive portfolio value directly from props using sell_price (liquidation value)
  const portfolioValue = useMemo(() => {
    return teams.reduce((acc, team) => {
      const markPrice = team.sell_price ?? team.price ?? 0;
      return acc + (team.shares || 0) * markPrice;
    }, 0);
  }, [teams]);

  const balance = cash + portfolioValue;

  const handleQuantityChange = (teamId, value) => {
    const num = Number(value);
    setQuantities((prev) => ({
      ...prev,
      [teamId]: value === "" ? "" : isNaN(num) ? 0 : num,
    }));
  };

  const handleBuy = (teamId, quantity) => {
    if (buyShares) {
      buyShares(teamId, quantity);
      setQuantities((prev) => ({ ...prev, [teamId]: "" }));
    }
  };

  const handleSell = (teamId, quantity) => {
    if (sellShares) {
      sellShares(teamId, quantity);
      setQuantities((prev) => ({ ...prev, [teamId]: "" }));
    }
  };

  // Market Recap Calculations for Top and Lowest Priced Teams
  const { topTeam, lowestTeam } = useMemo(() => {
    if (!teams.length) {
      return { topTeam: null, lowestTeam: null };
    }

    const sortedByPrice = [...teams].sort(
      (a, b) => (b.price ?? b.buy_price ?? 0) - (a.price ?? a.buy_price ?? 0)
    );
    const top = sortedByPrice[0];
    const lowest = sortedByPrice[sortedByPrice.length - 1];

    return { topTeam: top, lowestTeam: lowest };
  }, [teams]);

  // Derive most bought and most sold display objects from transaction props
  const mostBought = useMemo(() => {
    if (!propMostBought || propMostBought.length === 0) return null;
    const item = propMostBought[0];
    const teamObj = teams.find((t) => t.name === item.name);
    return {
      team: teamObj,
      name: item.name,
      amount: item.count,
    };
  }, [propMostBought, teams]);

  const mostSold = useMemo(() => {
    if (!propMostSold || propMostSold.length === 0) return null;
    const item = propMostSold[0];
    const teamObj = teams.find((t) => t.name === item.name);
    return {
      team: teamObj,
      name: item.name,
      amount: item.count,
    };
  }, [propMostSold, teams]);

  // Combined Conference, Search, and Field Sorting
  const filteredAndSortedTeams = useMemo(() => {
    const filtered = teams.filter((team) => {
      const matchesConference =
        selectedConference === "All" || team.conference === selectedConference;
      const matchesSearch =
        team.name?.toLowerCase().includes(searchTerm.toLowerCase().trim()) ||
        team.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase().trim());
      return matchesConference && matchesSearch;
    });

    return filtered.sort((a, b) => {
      let aVal;
      let bVal;

      if (sortField === "price") {
        aVal = a.price ?? a.buy_price ?? 0;
        bVal = b.price ?? b.buy_price ?? 0;
      } else if (sortField === "buy_price") {
        aVal = a.buy_price ?? a.price ?? 0;
        bVal = b.buy_price ?? b.price ?? 0;
      } else if (sortField === "sell_price") {
        aVal = a.sell_price ?? a.price ?? 0;
        bVal = b.sell_price ?? b.price ?? 0;
      } else if (sortField === "availableShares") {
        aVal = getAvailableShares(a);
        bVal = getAvailableShares(b);
      } else if (sortField === "name") {
        aVal = (a.name || "").toLowerCase();
        bVal = (b.name || "").toLowerCase();
      } else {
        aVal = a[sortField] ?? 0;
        bVal = b[sortField] ?? 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [teams, selectedConference, searchTerm, sortField, sortDirection]);

  // Keep active detail view in sync with real-time updates
  const currentSelectedTeam = selectedTeam
    ? teams.find((t) => getTeamKey(t) === getTeamKey(selectedTeam)) || selectedTeam
    : null;

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Top Header Row with Title and Get More Money Button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h2 style={{ margin: 0 }}>Market Dashboard</h2>
        <button
          onClick={() => {
            if (addCash) {
              addCash(0);
            }
          }}
          style={{
            backgroundColor: "#10b981",
            color: "#ffffff",
            border: "none",
            padding: "8px 14px",
            borderRadius: "6px",
            fontWeight: "600",
            cursor: "pointer",
            fontSize: "0.9rem",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            transition: "background-color 0.2s",
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#059669")}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#10b981")}
        >
          💵 Get $500 Cash
        </button>
      </div> */

      {/* Market Status Banner */}
      <div
        style={{
          padding: "0.75rem 1rem",
          marginBottom: "1.25rem",
          borderRadius: "6px",
          backgroundColor: marketOpen ? "#ebfbee" : "#fff5f5",
          border: `1px solid ${marketOpen ? "#b2f2bb" : "#ffc9c9"}`,
          color: marketOpen ? "#2b8a3e" : "#c92a2a",
          fontWeight: "600",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>
          {marketOpen
            ? "🟢 Market is Open — Trading Active"
            : "🔴 Market is Closed — Game Day Window Active"}
        </span>
      </div>

      {/* Balance Summary Header */}
      <p style={{ fontSize: "1.05rem", marginBottom: "1.25rem" }}>
        Cash Available: <strong>${cash.toFixed(2)}</strong> | Total Portfolio Value:{" "}
        <strong>${balance.toFixed(2)}</strong>
      </p>

      {currentSelectedTeam ? (
        <TeamDetail
          team={currentSelectedTeam}
          onBack={() => setSelectedTeam(null)}
          buyShares={handleBuy}
          sellShares={handleSell}
          marketOpen={marketOpen}
          cash={cash}
          sharesOwned={currentSelectedTeam.shares || 0}
        />
      ) : (
        <>
          {/* Market Recap Grid */}
          {teams.length > 0 && (
            <div style={recapBoxStyle}>
              {/* Most Valuable Card */}
              <div style={recapCardStyle}>
                <h4 style={{ margin: "0 0 6px 0", color: "#2b8a3e", fontSize: "0.85rem", textTransform: "uppercase" }}>
                  🔥 Most Valuable
                </h4>
                {topTeam ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {getTeamLogo(topTeam) && (
                      <img
                        src={getTeamLogo(topTeam)}
                        alt={topTeam.name}
                        referrerPolicy="no-referrer"
                        style={{ width: "24px", height: "24px", objectFit: "contain" }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <span style={{ fontWeight: "bold", fontSize: "0.95rem" }}>
                      {topTeam.name} (${(topTeam.price ?? topTeam.buy_price ?? 0).toFixed(2)})
                    </span>
                  </div>
                ) : (
                  <p style={{ margin: 0 }}>N/A</p>
                )}
              </div>

              {/* Lowest Priced Card */}
              <div style={recapCardStyle}>
                <h4 style={{ margin: "0 0 6px 0", color: "#c92a2a", fontSize: "0.85rem", textTransform: "uppercase" }}>
                  ❄️ Lowest Priced
                </h4>
                {lowestTeam ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {getTeamLogo(lowestTeam) && (
                      <img
                        src={getTeamLogo(lowestTeam)}
                        alt={lowestTeam.name}
                        referrerPolicy="no-referrer"
                        style={{ width: "24px", height: "24px", objectFit: "contain" }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <span style={{ fontWeight: "bold", fontSize: "0.95rem" }}>
                      {lowestTeam.name} (${(lowestTeam.price ?? lowestTeam.buy_price ?? 0).toFixed(2)})
                    </span>
                  </div>
                ) : (
                  <p style={{ margin: 0 }}>N/A</p>
                )}
              </div>

              {/* Most Bought Card */}
              <div style={recapCardStyle}>
                <h4 style={{ margin: "0 0 6px 0", color: "#1864ab", fontSize: "0.85rem", textTransform: "uppercase" }}>
                  📈 Most Bought
                </h4>
                {mostBought ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {getTeamLogo(mostBought.team) && (
                      <img
                        src={getTeamLogo(mostBought.team)}
                        alt={mostBought.name}
                        referrerPolicy="no-referrer"
                        style={{ width: "24px", height: "24px", objectFit: "contain" }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <span style={{ fontWeight: "bold", fontSize: "0.95rem" }}>
                      {mostBought.name} ({mostBought.amount} buys)
                    </span>
                  </div>
                ) : (
                  <p style={{ margin: 0, fontWeight: "500", color: "#6b7280" }}>None yet</p>
                )}
              </div>

              {/* Most Sold Card */}
              <div style={recapCardStyle}>
                <h4 style={{ margin: "0 0 6px 0", color: "#e67700", fontSize: "0.85rem", textTransform: "uppercase" }}>
                  📉 Most Sold
                </h4>
                {mostSold ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {getTeamLogo(mostSold.team) && (
                      <img
                        src={getTeamLogo(mostSold.team)}
                        alt={mostSold.name}
                        referrerPolicy="no-referrer"
                        style={{ width: "24px", height: "24px", objectFit: "contain" }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <span style={{ fontWeight: "bold", fontSize: "0.95rem" }}>
                      {mostSold.name} ({mostSold.amount} sells)
                    </span>
                  </div>
                ) : (
                  <p style={{ margin: 0, fontWeight: "500", color: "#6b7280" }}>None yet</p>
                )}
              </div>
            </div>
          )}

          {/* Filter Controls Row */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <ConferenceFilter
              selected={selectedConference}
              onChange={setSelectedConference}
            />

            <input
              type="text"
              placeholder="🔍 Search teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "0.95rem",
                width: "240px",
              }}
            />
          </div>

          {/* Teams Market Table */}
          {filteredAndSortedTeams.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                background: "#f9fafb",
                borderRadius: "8px",
                border: "1px dashed #d1d5db",
                color: "#6b7280",
              }}
            >
              No teams found matching your filters.
            </div>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th
                    style={sortableHeaderStyle}
                    onClick={() => handleSort("name")}
                  >
                    Team {renderSortIndicator("name")}
                  </th>
                  <th
                    style={sortableHeaderStyle}
                    onClick={() => handleSort("buy_price")}
                  >
                    Buy Price {renderSortIndicator("buy_price")}
                  </th>
                  <th
                    style={sortableHeaderStyle}
                    onClick={() => handleSort("sell_price")}
                  >
                    Sell Price {renderSortIndicator("sell_price")}
                  </th>
                  <th
                    style={sortableHeaderStyle}
                    onClick={() => handleSort("availableShares")}
                  >
                    Shares Available {renderSortIndicator("availableShares")}
                  </th>
                  <th style={{ ...thTdStyle, backgroundColor: "#f9fafb", fontWeight: "600" }}>
                    Quantity
                  </th>
                  <th style={{ ...thTdStyle, backgroundColor: "#f9fafb", fontWeight: "600" }}>
                    Buy
                  </th>
                  <th style={{ ...thTdStyle, backgroundColor: "#f9fafb", fontWeight: "600" }}>
                    Sell
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedTeams.map((team) => {
                  const key = getTeamKey(team);
                  return (
                    <TeamRow
                      key={key}
                      team={team}
                      quantity={quantities[key] || ""}
                      onChange={handleQuantityChange}
                      onBuy={handleBuy}
                      onSell={handleSell}
                      onSelect={setSelectedTeam}
                      sharesOwned={team.shares || 0}
                      marketOpen={marketOpen}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default Market;